import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Shield,
  Sparkles,
  Save,
  Loader2,
  X,
  Eye,
  EyeOff,
  Users,
  Key,
  Zap,
  Plus,
  Trash2,
  Search,
  Image as ImageIcon,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Coins,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { aiApi } from '../lib/api';
import { Select } from '../components/ui/Select';
import {
  CHATGPT_TEXT_MODELS,
  KIMI_TEXT_MODELS,
  openAiModelUsesDefaultTemperature,
  type AiTextModelInfo,
} from '../config/aiTextModels';
import { LabAiCostsPanel } from './super-admin-ai/LabAiCostsPanel';

interface AiProviderConfig {
  id?: string;
  name: string;
  displayName: string;
  apiKey: string;
  modelId: string;
  isActive: boolean;
  maxTokens: number;
  temperature: number;
}

interface UnlimitedUser {
  id: string;
  user: { id: string; email: string; firstName: string | null; lastName: string | null; role: string };
  tenant: { id: string; name: string };
  isUnlimited: boolean;
}

interface AllUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  tenantId: string;
  tenant: { name: string };
  aiTokenBalance: { balance: number; isUnlimited: boolean } | null;
}

interface ReviewGeneratorSettings {
  defaultImageProvider: string | null;
  defaultImageModel: string;
  reviewImageTemplates: Array<{ id: string; name: string; prompt: string }>;
}

interface AiActionDefinition {
  key: string;
  group: string;
  label: string;
  description: string;
  kind: 'text' | 'image';
}

interface AiActionModelsSettings {
  actions: AiActionDefinition[];
  mappings: Record<string, { provider: string | null; modelId: string | null }>;
}

const IMAGE_MODELS: ModelInfo[] = [
  { id: 'gpt-image-2', label: 'GPT Image 2', tier: 'premium', cost: 'wg rozmiaru', bestFor: 'Grafiki produktowe i kreacje reklamowe' },
];

interface ProviderTestState {
  status: 'testing' | 'success' | 'error';
  message: string;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  return (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;
}

const DEFAULT_PROVIDERS: AiProviderConfig[] = [
  { name: 'kimi', displayName: 'Kimi (Moonshot AI)', apiKey: '', modelId: 'kimi-k2.6', isActive: false, maxTokens: 4096, temperature: 1 },
  { name: 'claude', displayName: 'Claude', apiKey: '', modelId: 'claude-sonnet-4-6', isActive: false, maxTokens: 4096, temperature: 0.7 },
  { name: 'chatgpt', displayName: 'ChatGPT', apiKey: '', modelId: 'gpt-5.6-sol', isActive: false, maxTokens: 4096, temperature: 1 },
  { name: 'gemini', displayName: 'Gemini', apiKey: '', modelId: 'gemini-2.5-flash', isActive: false, maxTokens: 4096, temperature: 0.7 },
];

// Available models per provider — admin selects from these
// Updated August 2026
type ModelInfo = AiTextModelInfo;

const PROVIDER_MODELS: Record<string, ModelInfo[]> = {
  kimi: KIMI_TEXT_MODELS,
  claude: [
    { id: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5', tier: 'fast', cost: '$1 / $5', bestFor: 'Klasyfikacja, tagi, krótkie pola KB' },
    { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', tier: 'balanced', cost: '$3 / $15', bestFor: 'Opisy, advertoriale, landing page, ad copy' },
    { id: 'claude-opus-4-6', label: 'Opus 4.6', tier: 'premium', cost: '$5 / $25', bestFor: 'Złożone analizy, strategia, persona, USP' },
  ],
  chatgpt: CHATGPT_TEXT_MODELS,
  gemini: [
    { id: 'gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash Lite', tier: 'fast', cost: '$0.25 / $1.50', bestFor: 'Najtańszy — proste pola, tagowanie, bulk' },
    { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', tier: 'premium', cost: '~$2.50 / $15', bestFor: 'Najnowszy flagowiec — deep analysis, strategia' },
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', tier: 'fast', cost: '$0.15 / $0.60', bestFor: 'KB sekcje, listy, szybkie opisy' },
    { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', tier: 'fast', cost: '$0.08 / $0.30', bestFor: 'Ultrabudżetowy — formatowanie, tagi' },
    { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', tier: 'balanced', cost: '$1.25 / $10', bestFor: 'Advertoriale, landing, ad copy' },
    { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (dep. 06/2026)', tier: 'fast', cost: '$0.10 / $0.40', bestFor: 'Legacy — migruj do 2.5+' },
    { id: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite (dep. 06/2026)', tier: 'fast', cost: '$0.04 / $0.15', bestFor: 'Legacy — migruj do 2.5+' },
  ],
};

const TIER_INFO: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  fast: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Fast', icon: '$' },
  balanced: { color: 'text-accent-info', bg: 'bg-info/10', label: 'Balanced', icon: '$$' },
  premium: { color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Premium', icon: '$$$' },
  reasoning: { color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Reasoning', icon: '$$' },
};

export function SuperAdminAiPage() {
  const { user } = useAuthStore();
  if (user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />;
  }
  return <SuperAdminAiContent />;
}

export function SuperAdminAiContent() {
  const [activeTab, setActiveTab] = useState<'providers' | 'actionModels' | 'users' | 'reviewGenerator' | 'labCosts'>('providers');
  const [providers, setProviders] = useState<AiProviderConfig[]>(DEFAULT_PROVIDERS);
  const [unlimitedUsers, setUnlimitedUsers] = useState<UnlimitedUser[]>([]);
  const [allUsers, setAllUsers] = useState<AllUser[]>([]);
  const [reviewGeneratorSettings, setReviewGeneratorSettings] = useState<ReviewGeneratorSettings | null>(null);
  const [actionModelSettings, setActionModelSettings] = useState<AiActionModelsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savingDefaultProvider, setSavingDefaultProvider] = useState<string | null>(null);
  const [savingReviewGenerator, setSavingReviewGenerator] = useState(false);
  const [savingActionModels, setSavingActionModels] = useState(false);
  const [defaultProvider, setDefaultProvider] = useState<string | null>(null);
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [providerTests, setProviderTests] = useState<Record<string, ProviderTestState>>({});
  const initialProviderTestsStarted = useRef(false);

  // Grant tokens modal
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantData, setGrantData] = useState({ userId: '', tenantId: '', amount: 10000, description: '' });
  const [userSearch, setUserSearch] = useState('');

  // ESC closes modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showGrantModal) setShowGrantModal(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showGrantModal]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const testProviderConnection = async (provider: Pick<AiProviderConfig, 'name' | 'displayName'>): Promise<boolean> => {
    setProviderTests(prev => ({
      ...prev,
      [provider.name]: {
        status: 'testing',
        message: 'Sprawdzam zapisany klucz, model i parametry API...',
      },
    }));

    try {
      const response = await aiApi.testProvider(provider.name);
      const diagnostic = response.data.data;
      setProviderTests(prev => ({
        ...prev,
        [provider.name]: {
          status: 'success',
          message: `Połączenie działa. Model ${diagnostic.model} odpowiedział w ${diagnostic.latencyMs} ms.`,
        },
      }));
      return true;
    } catch (error) {
      const message = apiErrorMessage(error, `Nie udało się przetestować providera ${provider.displayName}.`);
      setProviderTests(prev => ({
        ...prev,
        [provider.name]: { status: 'error', message },
      }));
      return false;
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, unlimitedRes, allUsersRes, defaultProviderRes] = await Promise.all([
        aiApi.getAdminProviders(),
        aiApi.getUnlimitedUsers(),
        aiApi.getAllUsers(),
        aiApi.getDefaultProvider().catch(() => ({ data: { data: { provider: null } } })),
      ]);
      const [reviewSettingsRes, actionModelsRes] = await Promise.all([
        aiApi.getReviewGeneratorSettings().catch(() => null),
        aiApi.getActionModels().catch(() => null),
      ]);

      const existingProviders = providersRes.data.data as AiProviderConfig[];
      const merged = DEFAULT_PROVIDERS.map(def => {
        const existing = existingProviders.find((p: AiProviderConfig) => p.name === def.name);
        return existing ? { ...def, ...existing } : def;
      });

      setProviders(merged);
      setUnlimitedUsers(unlimitedRes.data.data);
      setAllUsers(allUsersRes.data.data);
      setDefaultProvider(defaultProviderRes.data.data?.provider || null);
      if (reviewSettingsRes?.data?.data) {
        setReviewGeneratorSettings(reviewSettingsRes.data.data);
      }
      if (actionModelsRes?.data?.data) {
        setActionModelSettings(actionModelsRes.data.data);
      }
      if (!initialProviderTestsStarted.current) {
        initialProviderTestsStarted.current = true;
        void Promise.all(
          merged
            .filter(provider => provider.isActive)
            .map(provider => testProviderConnection(provider)),
        );
      }
    } catch {
      showToast('Błąd ładowania konfiguracji AI', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveProvider = async (provider: AiProviderConfig) => {
    try {
      setSaving(provider.name);
      const parsedTemperature = Number(provider.temperature);
      const temperature = provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId)
        ? 1
        : Number.isFinite(parsedTemperature) ? parsedTemperature : 0.7;
      await aiApi.updateProvider(provider.name, {
        displayName: provider.displayName,
        apiKey: provider.apiKey.startsWith('••') ? undefined : provider.apiKey,
        modelId: provider.modelId,
        isActive: provider.isActive,
        maxTokens: Number(provider.maxTokens) || 4096,
        temperature,
      });

      if (provider.isActive) {
        const testPassed = await testProviderConnection(provider);
        showToast(
          testPassed
            ? `${provider.displayName}: konfiguracja zapisana i połączenie działa`
            : `${provider.displayName}: konfiguracja zapisana, ale test połączenia nie przeszedł`,
          testPassed ? 'success' : 'error',
        );
      } else {
        setProviderTests(prev => {
          const next = { ...prev };
          delete next[provider.name];
          return next;
        });
        showToast(`${provider.displayName}: konfiguracja zapisana`);
      }
      await loadData();
    } catch (error) {
      showToast(apiErrorMessage(error, `Błąd zapisu konfiguracji ${provider.displayName}`), 'error');
    } finally {
      setSaving(null);
    }
  };

  const addUnlimitedUser = async (userId: string, tenantId: string) => {
    try {
      await aiApi.addUnlimitedUser({ userId, tenantId });
      showToast('Użytkownik dodany do nieograniczonego dostępu');
      loadData();
    } catch {
      showToast('Błąd dodawania', 'error');
    }
  };

  const removeUnlimitedUser = async (userId: string) => {
    try {
      await aiApi.removeUnlimitedUser(userId);
      showToast('Nieograniczony dostęp usunięty');
      loadData();
    } catch {
      showToast('Błąd usuwania', 'error');
    }
  };

  const grantTokens = async () => {
    try {
      await aiApi.grantTokens(grantData);
      showToast(`Przyznano ${grantData.amount} tokenów`);
      setShowGrantModal(false);
      loadData();
    } catch {
      showToast('Błąd przyznawania tokenów', 'error');
    }
  };

  const setProviderAsDefault = async (provider: AiProviderConfig) => {
    if (!provider.isActive) {
      showToast('Najpierw aktywuj providera AI', 'error');
      return;
    }
    try {
      setSavingDefaultProvider(provider.name);
      const res = await aiApi.updateDefaultProvider(provider.name);
      setDefaultProvider(res.data.data?.provider || provider.name);
      showToast(`${provider.displayName} ustawiony jako domyślny model AI`);
    } catch {
      showToast('Błąd ustawiania domyślnego providera AI', 'error');
    } finally {
      setSavingDefaultProvider(null);
    }
  };

  const updateProvider = (name: string, field: string, value: unknown) => {
    setProviders(prev => prev.map(p =>
      p.name === name ? { ...p, [field]: value } : p
    ));
    setProviderTests(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const updateReviewGeneratorSettings = (patch: Partial<ReviewGeneratorSettings>) => {
    setReviewGeneratorSettings(prev => prev ? { ...prev, ...patch } : prev);
  };

  const updateReviewTemplate = (index: number, field: 'id' | 'name' | 'prompt', value: string) => {
    setReviewGeneratorSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        reviewImageTemplates: prev.reviewImageTemplates.map((template, i) => (
          i === index ? { ...template, [field]: value } : template
        )),
      };
    });
  };

  const saveReviewGeneratorSettings = async () => {
    if (!reviewGeneratorSettings) return;
    try {
      setSavingReviewGenerator(true);
      const res = await aiApi.updateReviewGeneratorSettings(reviewGeneratorSettings);
      setReviewGeneratorSettings(res.data.data);
      showToast('Ustawienia generatora opinii zapisane');
    } catch {
      showToast('Błąd zapisu ustawień generatora opinii', 'error');
    } finally {
      setSavingReviewGenerator(false);
    }
  };

  const updateActionModel = (key: string, patch: Partial<{ provider: string | null; modelId: string | null }>) => {
    setActionModelSettings(previous => {
      if (!previous) return previous;
      const current = previous.mappings[key] || { provider: null, modelId: null };
      return {
        ...previous,
        mappings: { ...previous.mappings, [key]: { ...current, ...patch } },
      };
    });
  };

  const saveActionModels = async () => {
    if (!actionModelSettings) return;
    try {
      setSavingActionModels(true);
      const response = await aiApi.updateActionModels(actionModelSettings.mappings);
      setActionModelSettings(response.data.data);
      showToast('Mapowanie modeli AI zostało zapisane');
    } catch (error) {
      showToast(apiErrorMessage(error, 'Nie udało się zapisać mapowania modeli AI'), 'error');
    } finally {
      setSavingActionModels(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  const filteredUsers = allUsers.filter(u =>
    !userSearch || u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-tertiary/20 to-accent-primary/20 border border-accent-tertiary/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-accent-tertiary" />
          </div>
          Konfiguracja AI
        </h1>
        <p className="text-sm text-text-secondary mt-1">Zarządzanie dostawcami AI i dostępem użytkowników</p>
      </div>

      {/* Tabs */}
      <div className="flex w-fit flex-wrap items-center gap-1 rounded-xl border border-border-subtle bg-surface-raised/50 p-1 animate-slide-up">
        <button
          onClick={() => setActiveTab('providers')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'providers'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <Key className="w-4 h-4" /> Dostawcy AI
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'users'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <Users className="w-4 h-4" /> Zarządzanie użytkownikami
        </button>
        <button
          onClick={() => setActiveTab('actionModels')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'actionModels'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <Sparkles className="w-4 h-4" /> Modele czynności
        </button>
        <button
          onClick={() => setActiveTab('reviewGenerator')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'reviewGenerator'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <ImageIcon className="w-4 h-4" /> Generator opinii
        </button>
        <button
          onClick={() => setActiveTab('labCosts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'labCosts'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <Coins className="w-4 h-4" /> Koszty Lab
        </button>
      </div>

      {/* Providers tab */}
      {activeTab === 'providers' && (
        <div className="space-y-4">
          {providers.map(provider => (
            <div key={provider.name} className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-border-subtle">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  provider.name === 'claude' ? 'bg-orange-500/10' :
                  provider.name === 'chatgpt' ? 'bg-success/10' :
                  'bg-info/10'
                }`}>
                  <Sparkles className={`w-5 h-5 ${
                    provider.name === 'claude' ? 'text-orange-400' :
                    provider.name === 'chatgpt' ? 'text-success' :
                    'text-accent-info'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-text-primary">{provider.displayName}</h3>
                  <p className="text-xs text-text-muted">{provider.name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={provider.isActive}
                      onChange={(e) => updateProvider(provider.name, 'isActive', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-surface-hover rounded-full peer peer-checked:bg-accent-primary/30 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                  </label>
                  <span className={`text-xs ${provider.isActive ? 'text-accent-primary' : 'text-text-muted'}`}>
                    {provider.isActive ? 'Aktywny' : 'Nieaktywny'}
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      Klucz API
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-56 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        Klucz uwierzytelniający do API providera. Pobierz z panelu: Anthropic Console, Google AI Studio lub OpenAI Platform.
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKeys[provider.name] ? 'text' : 'password'}
                        value={provider.apiKey}
                        onChange={(e) => updateProvider(provider.name, 'apiKey', e.target.value)}
                        className="input w-full pr-10"
                        placeholder="Wprowadź klucz API..."
                      />
                      <button
                        onClick={() => setShowApiKeys(prev => ({ ...prev, [provider.name]: !prev[provider.name] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                      >
                        {showApiKeys[provider.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      Model
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-64 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        Domyślny model dla tego providera. Fast = tanie i szybkie (do lekkich zadań). Balanced = dobry stosunek jakość/cena. Premium = najlepsza jakość (droższe). Można nadpisać per krok w pipeline.
                      </span>
                    </label>
                    {(() => {
                      const models = PROVIDER_MODELS[provider.name] || [];
                      const isCustom = models.length > 0 && !models.some(m => m.id === provider.modelId);
                      const selectedModel = models.find(m => m.id === provider.modelId);
                      return (
                        <div className="space-y-2">
                          <select
                            value={isCustom ? '__custom__' : provider.modelId}
                            onChange={(e) => {
                              if (e.target.value !== '__custom__') {
                                updateProvider(provider.name, 'modelId', e.target.value);
                              } else {
                                updateProvider(provider.name, 'modelId', '');
                              }
                            }}
                            className="input w-full"
                          >
                            {models.map(m => {
                              const ti = TIER_INFO[m.tier];
                              return (
                                <option key={m.id} value={m.id}>
                                  {ti?.icon} {m.label} — {m.cost} — {m.bestFor}
                                </option>
                              );
                            })}
                            <option value="__custom__">Inny model (wpisz ID)</option>
                          </select>
                          {selectedModel && (
                            <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${TIER_INFO[selectedModel.tier]?.bg} border border-border-subtle`}>
                              <span className={`text-xs font-medium ${TIER_INFO[selectedModel.tier]?.color}`}>
                                {TIER_INFO[selectedModel.tier]?.label}
                              </span>
                              <span className="text-[10px] text-text-secondary">|</span>
                              <span className="text-[10px] text-text-secondary">input/output: {selectedModel.cost} za 1M tok.</span>
                              <span className="text-[10px] text-text-secondary">|</span>
                              <span className="text-[10px] text-text-secondary">{selectedModel.bestFor}</span>
                            </div>
                          )}
                          {(isCustom || provider.modelId === '') && (
                            <input
                              type="text"
                              value={provider.modelId}
                              onChange={(e) => updateProvider(provider.name, 'modelId', e.target.value)}
                              className="input w-full"
                              placeholder="np. gemini-2.5-pro-preview"
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      Maks. tokenów
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-64 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        Maksymalna długość odpowiedzi AI (nie wliczając promptu). 4096 ≈ ~3000 słów. Zwiększ do 8192 jeśli odpowiedzi się urywają. Dla krótkich zadań (nazwy, nagłówki) wystarczy 1000-2000.
                      </span>
                    </label>
                    <input
                      type="number"
                      value={provider.maxTokens}
                      onChange={(e) => updateProvider(provider.name, 'maxTokens', parseInt(e.target.value))}
                      className="input w-full"
                      min={100}
                      max={128000}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      {provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId)
                        ? 'Temperatura (stała)'
                        : 'Temperatura (0-2)'}
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-64 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        {provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId)
                          ? 'Modele GPT-5 obsługują w tym API wyłącznie domyślną temperaturę 1. EcomFlow nie wysyła tego parametru, żeby zachować zgodność modelu.'
                          : 'Kontroluje kreatywność AI. 0 = deterministyczne (identyczne odpowiedzi). 0.3-0.5 = precyzyjne (analizy, specyfikacje). 0.7 = balans (opisy, KB). 1.0+ = kreatywne (ad copy, nagłówki). Można nadpisać per krok w pipeline.'}
                      </span>
                    </label>
                    <input
                      type="number"
                      value={provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId) ? 1 : provider.temperature}
                      onChange={(e) => updateProvider(provider.name, 'temperature', parseFloat(e.target.value))}
                      className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                      min={0}
                      max={2}
                      step={0.1}
                      disabled={provider.name === 'kimi' || (provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId))}
                    />
                    {provider.name === 'kimi' && <p className="text-xs text-text-muted">Kimi dobiera temperaturę do trybu modelu. Klucz API: platform.kimi.ai (konto międzynarodowe).</p>}
                    {provider.name === 'chatgpt' && openAiModelUsesDefaultTemperature(provider.modelId) && (
                      <p className="mt-1 text-[10px] text-text-muted">
                        GPT-5.6 używa wartości domyślnej. Ustawienia pipeline'ów nie nadpisują jej w zapytaniu do OpenAI.
                      </p>
                    )}
                  </div>
                </div>

                {providerTests[provider.name] && (
                  <div
                    role={providerTests[provider.name].status === 'error' ? 'alert' : 'status'}
                    className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-xs ${
                      providerTests[provider.name].status === 'success'
                        ? 'border-success/30 bg-success/10 text-success'
                        : providerTests[provider.name].status === 'error'
                          ? 'border-danger/30 bg-danger/10 text-danger'
                          : 'border-accent-info/30 bg-info/10 text-accent-info'
                    }`}
                  >
                    {providerTests[provider.name].status === 'testing' ? (
                      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                    ) : providerTests[provider.name].status === 'success' ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <div>
                      <p className="font-medium">
                        {providerTests[provider.name].status === 'testing'
                          ? 'Testuję połączenie AI'
                          : providerTests[provider.name].status === 'success'
                            ? 'Konfiguracja AI działa'
                            : 'Konfiguracja AI wymaga poprawy'}
                      </p>
                      <p className="mt-0.5 opacity-90">{providerTests[provider.name].message}</p>
                    </div>
                  </div>
                )}

                {/* Model tier legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 py-2 text-[10px]">
                  {Object.entries(TIER_INFO).map(([key, ti]) => (
                    <span key={key} className="flex items-center gap-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${ti.bg} border ${ti.color.replace('text-', 'border-')}`} />
                      <span className={ti.color}>{ti.label}</span>
                      <span className="text-text-disabled">({ti.icon})</span>
                      <span className="text-text-muted">
                        {key === 'fast' && '— tanie, szybkie, do prostych pól'}
                        {key === 'balanced' && '— optymalny koszt/jakość'}
                        {key === 'premium' && '— najlepsza jakość, drogie'}
                        {key === 'reasoning' && '— rozumowanie krok po kroku'}
                      </span>
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    {defaultProvider === provider.name ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10 border border-success/30 text-xs font-medium text-success">
                        <Sparkles className="w-3.5 h-3.5" />
                        Domyślny
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setProviderAsDefault(provider)}
                        disabled={!provider.isActive || savingDefaultProvider === provider.name}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-subtle bg-surface-hover/70 text-xs font-medium text-text-secondary hover:text-text-primary hover:border-accent-primary/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {savingDefaultProvider === provider.name ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        Ustaw jako domyślne
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => saveProvider(provider)}
                    disabled={saving === provider.name}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    {saving === provider.name ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Zapisz {provider.displayName}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'actionModels' && actionModelSettings && (
        <div className="space-y-5">
          <div className="rounded-xl border border-accent-primary/20 bg-accent-primary/5 p-4">
            <h2 className="text-sm font-semibold text-text-primary">Jedno źródło prawdy dla modeli AI</h2>
            <p className="mt-1 text-xs leading-relaxed text-text-secondary">
              Przypisanie w tym miejscu ma pierwszeństwo przed ustawieniem modułu lub kroku pipeline. Puste przypisanie korzysta z domyślnego dostawcy systemowego, a jeśli go nie wybrano, z ustawienia modułu. Błąd nie przełącza procesu na innego dostawcę. Wyszukiwanie źródeł ma osobne przypisanie.
            </p>
          </div>

          {Array.from(new Set(actionModelSettings.actions.map(action => action.group))).map(group => (
            <section key={group} className="overflow-hidden rounded-xl border border-border-subtle bg-surface-raised/50">
              <div className="border-b border-border-subtle px-5 py-3">
                <h3 className="text-sm font-semibold text-text-primary">{group}</h3>
              </div>
              <div className="divide-y divide-border-subtle">
                {actionModelSettings.actions.filter(action => action.group === group).map(action => {
                  const mapping = actionModelSettings.mappings[action.key] || { provider: null, modelId: null };
                  const effectiveProvider = mapping.provider || (action.key === 'ai_web_search' ? 'gemini' : action.kind === 'image' ? 'chatgpt' : defaultProvider) || '';
                  const availableModels = action.kind === 'image' ? IMAGE_MODELS : (PROVIDER_MODELS[effectiveProvider] || []);
                  const customModel = mapping.modelId && !availableModels.some(model => model.id === mapping.modelId)
                    ? mapping.modelId
                    : null;
                  return (
                    <div key={action.key} className="grid gap-3 px-5 py-4 lg:grid-cols-[minmax(240px,1fr)_220px_minmax(280px,1fr)] lg:items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-text-primary">{action.label}</span>
                          <span className="rounded-full border border-border-subtle px-1.5 py-0.5 text-[9px] uppercase text-text-muted">{action.kind === 'image' ? 'grafika' : 'tekst'}</span>
                        </div>
                        <p className="mt-1 text-xs text-text-muted">{action.description}</p>
                      </div>
                      <Select
                        value={mapping.provider || ''}
                        onChange={event => updateActionModel(action.key, { provider: event.target.value || null, modelId: null })}
                        aria-label={`Provider: ${action.label}`}
                      >
                        <option value="">{action.key === 'ai_web_search' ? 'Wyszukiwanie: Gemini' : action.kind === 'image' ? 'Domyślny dostawca grafiki: OpenAI' : `Domyślny tekstowy: ${defaultProvider || 'nie ustawiono'}`}</option>
                        {providers
                          .filter(provider => provider.isActive && (action.kind === 'text' || provider.name === 'chatgpt') && (action.key !== 'ai_web_search' || ['gemini', 'chatgpt'].includes(provider.name)))
                          .map(provider => <option key={provider.name} value={provider.name}>{provider.displayName}</option>)}
                      </Select>
                      <Select
                        value={mapping.modelId || ''}
                        onChange={event => updateActionModel(action.key, { modelId: event.target.value || null })}
                        disabled={!mapping.provider}
                        aria-label={`Model: ${action.label}`}
                      >
                        <option value="">{mapping.provider ? 'Domyślny model providera' : 'Najpierw wybierz provider'}</option>
                        {availableModels.map(model => <option key={model.id} value={model.id}>{model.label} - {model.bestFor}</option>)}
                        {customModel && <option value={customModel}>{customModel}</option>}
                      </Select>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveActionModels}
              disabled={savingActionModels}
              className="btn-primary inline-flex items-center gap-2 text-sm"
            >
              {savingActionModels ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Zapisz mapowanie modeli
            </button>
          </div>
        </div>
      )}

      {activeTab === 'labCosts' && <LabAiCostsPanel />}

      {/* Users tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Unlimited access users */}
          <div className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-border-subtle">
              <Zap className="w-5 h-5 text-accent-primary" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-text-primary">Nieograniczony dostęp AI</h3>
                <p className="text-xs text-text-secondary">Konta prywatne z nielimitowanym dostępem do AI</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {unlimitedUsers.length > 0 ? (
                unlimitedUsers.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface-raised/50 border border-border-subtle">
                    <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-accent-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-text-primary">{item.user.email}</p>
                      <p className="text-xs text-text-muted">{item.user.firstName} {item.user.lastName} — {item.tenant.name}</p>
                    </div>
                    <button
                      onClick={() => removeUnlimitedUser(item.user.id)}
                      className="btn-ghost p-1.5 text-danger"
                      title="Usuń nieograniczony dostęp"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text-secondary text-center py-4">Brak użytkowników z nieograniczonym dostępem</p>
              )}
            </div>
          </div>

          {/* All users with token balances */}
          <div className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-border-subtle">
              <Users className="w-5 h-5 text-accent-tertiary" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-text-primary">Wszyscy użytkownicy</h3>
                <p className="text-xs text-text-secondary">Zarządzaj tokenami i dostępem AI</p>
              </div>
              <button
                onClick={() => setShowGrantModal(true)}
                className="btn-primary text-sm flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Przyznaj tokeny
              </button>
            </div>
            <div className="p-5">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="input w-full pl-10"
                  placeholder="Szukaj użytkownika..."
                />
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface-raised/50 border border-border-subtle">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary truncate">{u.email}</p>
                      <p className="text-xs text-text-muted">
                        {u.firstName} {u.lastName} — {u.tenant.name} — <span className="text-text-secondary">{u.role}</span>
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {u.aiTokenBalance?.isUnlimited ? (
                        <span className="text-xs text-accent-primary font-semibold flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Unlimited
                        </span>
                      ) : (
                        <span className="text-xs text-text-secondary">
                          {(u.aiTokenBalance?.balance || 0).toLocaleString('pl-PL')} tokenów
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!u.aiTokenBalance?.isUnlimited && (
                        <button
                          onClick={() => addUnlimitedUser(u.id, u.tenantId)}
                          className="btn-ghost p-1.5 text-accent-primary"
                          title="Nadaj nieograniczony dostęp"
                        >
                          <Zap className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setGrantData({ userId: u.id, tenantId: u.tenantId, amount: 10000, description: '' });
                          setShowGrantModal(true);
                        }}
                        className="btn-ghost p-1.5"
                        title="Przyznaj tokeny"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reviewGenerator' && reviewGeneratorSettings && (
        <div className="space-y-4">
          <div className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-border-subtle">
              <ImageIcon className="w-5 h-5 text-accent-primary" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-text-primary">Obrazy opinii AI</h3>
                <p className="text-xs text-text-secondary">Domyślny dostawca i model obrazów, także w Laboratorium. Niezależne od dostawcy tekstowego (np. Kimi).</p>
              </div>
              <button
                onClick={saveReviewGeneratorSettings}
                disabled={savingReviewGenerator}
                className="btn-primary text-sm flex items-center gap-2"
              >
                {savingReviewGenerator ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Zapisz ustawienia
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Provider grafiki</label>
                <select
                  value={reviewGeneratorSettings.defaultImageProvider || ''}
                  onChange={(e) => updateReviewGeneratorSettings({ defaultImageProvider: e.target.value || null })}
                  className="input w-full"
                >
                  <option value="">Brak</option>
                  {providers.filter(provider => provider.isActive).map(provider => (
                    <option key={provider.name} value={provider.name}>{provider.displayName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Model grafiki</label>
                <input
                  value={reviewGeneratorSettings.defaultImageModel}
                  onChange={(e) => updateReviewGeneratorSettings({ defaultImageModel: e.target.value })}
                  className="input w-full"
                  placeholder="gpt-image-2"
                />
              </div>
            </div>
          </div>

          <div className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-border-subtle">
              <FileText className="w-5 h-5 text-accent-tertiary" />
              <div>
                <h3 className="text-base font-semibold text-text-primary">Szablony promptów obrazów</h3>
                <p className="text-xs text-text-secondary">Dostępne w modalu generowania obrazu przy konkretnej opinii</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {reviewGeneratorSettings.reviewImageTemplates.map((template, index) => (
                <div key={`${template.id}-${index}`} className="rounded-xl border border-border-subtle bg-surface-base/40 p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3">
                    <div>
                      <label className="text-xs text-text-secondary mb-1 block">ID</label>
                      <input value={template.id} onChange={(e) => updateReviewTemplate(index, 'id', e.target.value)} className="input w-full" />
                    </div>
                    <div>
                      <label className="text-xs text-text-secondary mb-1 block">Nazwa</label>
                      <input value={template.name} onChange={(e) => updateReviewTemplate(index, 'name', e.target.value)} className="input w-full" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block">Prompt</label>
                    <textarea
                      value={template.prompt}
                      onChange={(e) => updateReviewTemplate(index, 'prompt', e.target.value)}
                      className="input w-full font-mono text-xs"
                      rows={5}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grant tokens modal */}
      {showGrantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h3 className="text-lg font-semibold text-text-primary">Przyznaj tokeny AI</h3>
              <button onClick={() => setShowGrantModal(false)} className="btn-ghost p-1.5">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {grantData.userId ? (
                <div className="text-sm text-text-secondary">
                  Użytkownik: <span className="text-text-primary font-medium">
                    {allUsers.find(u => u.id === grantData.userId)?.email}
                  </span>
                </div>
              ) : (
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Wybierz użytkownika</label>
                  <select
                    value={grantData.userId}
                    onChange={(e) => {
                      const selectedUser = allUsers.find(u => u.id === e.target.value);
                      setGrantData(prev => ({ ...prev, userId: e.target.value, tenantId: selectedUser?.tenantId || '' }));
                    }}
                    className="input w-full"
                  >
                    <option value="">— Wybierz —</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.email} ({u.tenant.name})</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Ilość tokenów</label>
                <input
                  type="number"
                  value={grantData.amount}
                  onChange={(e) => setGrantData(prev => ({ ...prev, amount: parseInt(e.target.value) }))}
                  className="input w-full"
                  min={1}
                />
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Opis (opcjonalnie)</label>
                <input
                  type="text"
                  value={grantData.description}
                  onChange={(e) => setGrantData(prev => ({ ...prev, description: e.target.value }))}
                  className="input w-full"
                  placeholder="np. Bonus powitalny"
                />
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-border-subtle">
              <button
                onClick={grantTokens}
                disabled={!grantData.userId || !grantData.amount}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Przyznaj {grantData.amount.toLocaleString('pl-PL')} tokenów
              </button>
              <button onClick={() => setShowGrantModal(false)} className="btn-secondary flex-1">Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border shadow-lg ${
          toast.type === 'success'
            ? 'bg-success/10 border-success/30 text-success'
            : 'bg-danger/10 border-danger/30 text-danger'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
